import React, { useState, useEffect, useRef } from 'react';
import * as Pitchfinder from 'pitchfinder';
import ReactSpeedometer from 'react-d3-speedometer';

const Tuner = () => {
  const [detectedPitch, setDetectedPitch] = useState(null); // Track the detected pitch
  const [closestPitch, setClosestPitch] = useState(''); // Closest musical pitch
  const [pitchError, setPitchError] = useState(0); // Error from closest pitch
  const [microphoneError, setMicrophoneError] = useState(null); // Track microphone access errors
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null); // For microphone input analysis
  const pitchDetectorRef = useRef(null); // For pitch detection

  // Pitches and frequencies from G3 to G6 (including half tones)
  const referencePitches = [
    'G3', 'G#3', 'A3', 'A#3', 'B3',
    'C4', 'C#4', 'D4', 'D#4', 'E4',
    'F4', 'F#4', 'G4', 'G#4', 'A4',
    'A#4', 'B4', 'C5', 'C#5', 'D5',
    'D#5', 'E5', 'F5', 'F#5', 'G5',
    'G#5', 'A5', 'A#5', 'B5', 'C6',
    'C#6', 'D6', 'D#6', 'E6', 'F6',
    'F#6', 'G6'
  ];

  // Corresponding frequencies for the pitches
  const noteFrequencies = {
    'G3': 196.00, 'G#3': 207.65, 'A3': 220.00, 'A#3': 233.08, 'B3': 246.94,
    'C4': 261.63, 'C#4': 277.18, 'D4': 293.66, 'D#4': 311.13, 'E4': 329.63,
    'F4': 349.23, 'F#4': 369.99, 'G4': 392.00, 'G#4': 415.30, 'A4': 440.00,
    'A#4': 466.16, 'B4': 493.88, 'C5': 523.25, 'C#5': 554.37, 'D5': 587.33,
    'D#5': 622.25, 'E5': 659.25, 'F5': 698.46, 'F#5': 739.99, 'G5': 783.99,
    'G#5': 830.61, 'A5': 880.00, 'A#5': 932.33, 'B5': 987.77, 'C6': 1046.50,
    'C#6': 1108.73, 'D6': 1174.66, 'D#6': 1244.51, 'E6': 1318.51, 'F6': 1396.91,
    'F#6': 1479.98, 'G6': 1567.98
  };

  // Set up the AudioContext
  useEffect(() => {
    const init = async () => {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioContext;
    };
    init();
  }, []);

  // Set up the microphone for pitch detection
  useEffect(() => {
    const getMicrophone = async () => {
      try {
        const audioContext = audioContextRef.current;

        // Request microphone access
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

        const analyser = audioContext.createAnalyser();
        const source = audioContext.createMediaStreamSource(stream);
        source.connect(analyser);
        analyser.fftSize = 2048;
        analyserRef.current = analyser;
        // pitchDetectorRef.current = Pitchfinder.AMDF();
        // Use the Pitchfinder AMDF algorithm for pitch detection
        pitchDetectorRef.current = Pitchfinder.AMDF({
          sampleRate: audioContext.sampleRate, // Set sample rate
          minFrequency: 100, // G3 as minimum frequency
          maxFrequency: 1419 // E6 as maximum frequency
        });

        const buffer = new Float32Array(analyser.fftSize);
        const detectPitch = () => {
          analyser.getFloatTimeDomainData(buffer);
          const pitch = pitchDetectorRef.current(buffer);
          if (pitch) {
            setDetectedPitch(pitch); // Update detected pitch
            calculateClosestPitch(pitch); // Calculate the closest pitch and error
          }
          requestAnimationFrame(detectPitch);
        };
        detectPitch();
      } catch (error) {
        console.error("Error accessing microphone:", error);
        setMicrophoneError("No microphone detected. Please connect a microphone.");
      }
    };

    getMicrophone();
  }, []);

  // Calculate the closest pitch and error
  const calculateClosestPitch = (detectedPitch) => {
    let closestNote = '';
    let smallestDifference = Infinity;
    console.log(detectedPitch)
    // Find the closest pitch
    referencePitches.forEach((note) => {
      const difference = Math.abs(detectedPitch - noteFrequencies[note]);
      if (difference < smallestDifference) {
        smallestDifference = difference;
        closestNote = note;
      }
    });

    setClosestPitch(closestNote);
    const error = ((detectedPitch - noteFrequencies[closestNote]) / noteFrequencies[closestNote]) * 100;
    setPitchError(error);
  };

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <h2 style={styles.title}>Pitch Detection</h2>
        <p style={styles.description}>
          {microphoneError 
            ? "Connect a microphone to enable real-time pitch analysis and tuning guidance."
            : "Play your instrument near the microphone to see real-time pitch analysis and tuning guidance."
          }
        </p>
      </div>

      {/* Pitch detection gauge */}
      <div style={styles.gaugeContainer}>
        <div style={microphoneError ? styles.disabledGauge : styles.enabledGauge}>
          <ReactSpeedometer
            value={microphoneError ? 0 : pitchError}
            minValue={-50} // 50% below the correct pitch
            maxValue={50} // 50% above the correct pitch
            needleColor={microphoneError ? "#9CA3AF" : "red"}
            startColor={microphoneError ? "#F3F4F6" : "green"}
            endColor={microphoneError ? "#F3F4F6" : "blue"}
            segments={10}
            currentValueText={
              microphoneError 
                ? "Microphone required for pitch detection" 
                : `Closest Pitch: ${closestPitch} | Error: ${Math.round(pitchError)}%`
            }
            textColor={microphoneError ? "#9CA3AF" : "#1F2937"}
          />
          {microphoneError && (
            <div style={styles.disabledOverlay}>
              <div style={styles.disabledMessage}>
                🔇 Microphone access required
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Tuner;

// Styles matching the app design system
const styles = {
  card: {
    padding: 'clamp(24px, 4vw, 32px)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--surface-border)',
    background: 'var(--surface)',
    boxShadow: 'var(--shadow-lg)',
  },
  header: {
    textAlign: 'center',
    marginBottom: '24px',
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    color: 'var(--text-primary)',
    margin: '0 0 8px 0',
  },
  description: {
    fontSize: '16px',
    color: 'var(--text-secondary)',
    lineHeight: '1.5',
    margin: 0,
    maxWidth: '600px',
    margin: '0 auto',
  },
  gaugeContainer: {
    display: 'flex',
    justifyContent: 'center',
    position: 'relative',
  },
  enabledGauge: {
    position: 'relative',
  },
  disabledGauge: {
    position: 'relative',
    filter: 'grayscale(100%) opacity(0.6)',
  },
  disabledOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    borderRadius: 'var(--radius-md)',
  },
  disabledMessage: {
    backgroundColor: 'var(--surface-strong)',
    color: 'var(--text-secondary)',
    padding: '8px 16px',
    borderRadius: 'var(--radius-md)',
    fontSize: '14px',
    fontWeight: '500',
    border: '1px solid var(--surface-border)',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
};